/**
 * ProfileDrawer — right-side panel for admins + super-admins to view a
 * teammate's full employee profile.
 *
 * Mode picks the endpoint:
 *   admin       → /admin/members/{user_id}/profile (tenant-scoped check)
 *   superadmin  → /superadmin/users/{user_id}/profile (cross-tenant)
 *
 * Read-only. Edits happen via the user's own /settings/profile.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type { EmployeeProfile } from "@/lib/types";

type Mode = "admin" | "superadmin";

type Props = {
  userId: string;
  displayName: string;
  mode: Mode;
  onClose: () => void;
};

const VISA_LABEL: Record<string, string> = {
  us_citizen: "US Citizen",
  green_card: "Green Card / Permanent Resident",
  h1b: "H1B",
  h4_ead: "H4-EAD",
  l2_ead: "L2-EAD",
  opt: "F-1 OPT",
  cpt: "F-1 CPT",
  tn: "TN",
  other: "Other",
};

export function ProfileDrawer({ userId, displayName, mode, onClose }: Props) {
  const path =
    mode === "superadmin"
      ? `/superadmin/users/${userId}/profile`
      : `/admin/members/${userId}/profile`;

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-profile", mode, userId],
    queryFn: async () => (await api.get<EmployeeProfile>(path)).data,
  });

  // Reset two-factor auth — for when this person lost their phone AND recovery
  // codes. Tenant admin hits the tenant-scoped route; super-admin the global one.
  const resetPath =
    mode === "superadmin"
      ? `/superadmin/users/${userId}/reset-mfa`
      : `/admin/members/${userId}/reset-mfa`;
  const [resetMsg, setResetMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const resetMfa = useMutation({
    mutationFn: async () => {
      await api.post(resetPath);
    },
    onSuccess: () =>
      setResetMsg({
        ok: true,
        text: `Done — ${displayName} will set up a new authenticator on their next sign-in.`,
      }),
    onError: (e) =>
      setResetMsg({ ok: false, text: extractErrorMessage(e, "Could not reset two-factor auth.") }),
  });
  const onReset = () => {
    if (
      window.confirm(
        `Reset two-factor authentication for ${displayName}? Their current authenticator stops working and they set up a new one on next sign-in.`,
      )
    ) {
      setResetMsg(null);
      resetMfa.mutate();
    }
  };

  // ESC closes the drawer.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div style={backdrop} onClick={onClose}>
      <aside
        role="dialog"
        aria-label={`${displayName} profile`}
        style={drawer}
        onClick={(e) => e.stopPropagation()}
      >
        <header style={header}>
          <div>
            <div style={{ fontSize: 11, color: "var(--mute)", letterSpacing: 0.6, textTransform: "uppercase" }}>
              Profile
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>
              {displayName}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={closeBtn}>
            ×
          </button>
        </header>

        <div style={body}>
          {isLoading && <p style={muted}>Loading…</p>}
          {error && (
            <p style={{ ...muted, color: "#ee1d36" }}>
              Could not load this profile.
            </p>
          )}
          {data && (
            <>
              <Section title="Contact">
                <Field label="Job ID" value={data.job_id} mono />
                <Field label="Designation" value={data.designation} />
                <Field label="Phone" value={data.phone} />
                <Field label="Address" value={data.address} multiline />
              </Section>

              <Section title="Resume">
                {data.resume_filename ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{data.resume_filename}</div>
                      <div style={muted}>
                        {data.resume_size_bytes
                          ? `${(data.resume_size_bytes / 1024).toFixed(1)} KB`
                          : ""}
                        {data.resume_uploaded_at
                          ? ` · ${new Date(data.resume_uploaded_at).toLocaleDateString()}`
                          : ""}
                      </div>
                    </div>
                    {data.resume_download_url && (
                      <a
                        href={data.resume_download_url}
                        target="_blank"
                        rel="noreferrer"
                        style={downloadBtn}
                      >
                        Download
                      </a>
                    )}
                  </div>
                ) : (
                  <p style={muted}>No resume uploaded yet.</p>
                )}
              </Section>

              <Section title="Work authorisation">
                <Field
                  label="Visa status"
                  value={data.visa_status ? VISA_LABEL[data.visa_status] ?? data.visa_status : null}
                />
                <Field label="Visa expiry" value={data.visa_expiry} />
              </Section>

              <Section title="Professional">
                <Field
                  label="LinkedIn"
                  value={data.linkedin_url}
                  href={data.linkedin_url}
                />
                <Field
                  label="GitHub"
                  value={data.github_url}
                  href={data.github_url}
                />
                <div style={field}>
                  <span style={fieldLabel}>GitHub PAT (coding access)</span>
                  <span style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 9999,
                        background: data.github_connected ? "#00d722" : "var(--mute)",
                      }}
                    />
                    {data.github_connected ? (
                      <>
                        Connected
                        {data.github_pat_username ? ` as @${data.github_pat_username}` : ""}
                        {" · commit email "}
                        {data.github_pat_email ? "resolved" : "not resolved"}
                      </>
                    ) : (
                      "Not connected"
                    )}
                  </span>
                </div>
                <Field
                  label="Skills"
                  value={data.skills && data.skills.length ? data.skills.join(", ") : null}
                />
                <Field
                  label="Years of experience"
                  value={data.years_experience?.toString()}
                />
                {data.education && data.education.length > 0 && (
                  <div style={field}>
                    <span style={fieldLabel}>Education</span>
                    <ul style={{ margin: "4px 0 0", paddingLeft: 16, fontSize: 13 }}>
                      {data.education.map((e, i) => (
                        <li key={i}>
                          {e.degree} · {e.university} ({e.year})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>

              <Section title="Personal">
                <Field label="Date of birth" value={data.date_of_birth} />
                {data.emergency_contact && (
                  <div style={field}>
                    <span style={fieldLabel}>Emergency contact</span>
                    <div style={{ fontSize: 13 }}>
                      {data.emergency_contact.name} ({data.emergency_contact.relation}) · {data.emergency_contact.phone}
                    </div>
                  </div>
                )}
              </Section>

              <Section title="Staffing">
                <Field
                  label="Hourly rate"
                  value={data.hourly_rate_usd ? `$${data.hourly_rate_usd}/hr` : null}
                />
                <Field
                  label="Salary expectation"
                  value={data.salary_expectation_usd ? `$${data.salary_expectation_usd}` : null}
                />
                <Field
                  label="Notice period"
                  value={data.notice_period_days != null ? `${data.notice_period_days} days` : null}
                />
              </Section>

              {!data.is_complete && (
                <div style={pending}>
                  This profile is incomplete — the employee still needs to add{" "}
                  {!data.phone && "phone, "}
                  {!data.address && "address, "}
                  {!data.resume_object_key && "resume"}
                  .
                </div>
              )}
            </>
          )}

          <Section title="Security">
            <div style={field}>
              <span style={fieldLabel}>Two-factor auth</span>
              <p style={{ ...muted, margin: "2px 0 8px" }}>
                Reset only if they lost their phone and recovery codes. Their
                current authenticator stops working; they set up a new one on next
                sign-in.
              </p>
              <button
                type="button"
                onClick={onReset}
                disabled={resetMfa.isPending}
                style={resetBtn(resetMfa.isPending)}
              >
                {resetMfa.isPending ? "Resetting…" : "Reset two-factor auth"}
              </button>
              {resetMsg && (
                <p
                  role={resetMsg.ok ? "status" : "alert"}
                  style={{ fontSize: 12.5, marginTop: 8, color: resetMsg.ok ? "#047a18" : "#ee1d36" }}
                >
                  {resetMsg.text}
                </p>
              )}
            </div>
          </Section>
        </div>
      </aside>
    </div>
  );
}

// ---------- helpers ----------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 20 }}>
      <div style={sectionTitle}>{title}</div>
      <div style={sectionBody}>{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  href,
  multiline,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  href?: string | null;
  multiline?: boolean;
  mono?: boolean;
}) {
  if (!value) {
    return (
      <div style={field}>
        <span style={fieldLabel}>{label}</span>
        <span style={muted}>—</span>
      </div>
    );
  }
  return (
    <div style={field}>
      <span style={fieldLabel}>{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" style={{ color: "var(--ink)", fontSize: 13 }}>
          {value}
        </a>
      ) : (
        <span
          style={{
            fontSize: 13,
            whiteSpace: multiline ? "pre-wrap" : undefined,
            fontFamily: mono ? "var(--font-mono)" : undefined,
          }}
        >
          {value}
        </span>
      )}
    </div>
  );
}

// ---------- styles ----------

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  zIndex: 100,
  display: "flex",
  justifyContent: "flex-end",
};

const drawer: CSSProperties = {
  width: "100%",
  maxWidth: 480,
  height: "100%",
  background: "var(--canvas)",
  borderLeft: "1px solid var(--hairline)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const header: CSSProperties = {
  padding: "20px 24px",
  borderBottom: "1px solid var(--hairline)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "var(--surface-1)",
};

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 24,
  lineHeight: 1,
  cursor: "pointer",
  color: "var(--mute)",
  padding: 4,
};

const body: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: 24,
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 8,
  paddingBottom: 6,
  borderBottom: "1px solid var(--hairline)",
};

const sectionBody: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const field: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const fieldLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "var(--mute)",
};

const muted: CSSProperties = {
  color: "var(--mute)",
  fontSize: 13,
};

const downloadBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  padding: "6px 12px",
  borderRadius: 4,
  fontSize: 12,
  textDecoration: "none",
};

const pending: CSSProperties = {
  background: "rgba(255,174,19,0.10)",
  border: "1px solid rgba(255,174,19,0.3)",
  borderRadius: 4,
  padding: "10px 14px",
  fontSize: 13,
  color: "#8a5b00",
};

function resetBtn(disabled: boolean): CSSProperties {
  return {
    alignSelf: "flex-start",
    background: "var(--canvas)",
    color: "var(--ink)",
    border: "1px solid var(--hairline)",
    borderRadius: 4,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 500,
    cursor: disabled ? "wait" : "pointer",
    opacity: disabled ? 0.6 : 1,
  };
}
